import { api } from 'shared/api/api';
import { EntityStore } from '../../../shared/stores/EntityStore';
import { action, makeObservable, runInAction, override } from 'mobx';
import { buildFormData } from 'ats/utils/buildFormData';

class ApplicantStore extends EntityStore {
	constructor() {
		super('applicants');
		makeObservable(this);
	}

	taskOptions = [
		{ label: 'HR Interview', value: 'hr_interview' },
		{ label: 'Test', value: 'test' },
		{ label: 'Coding Challenge', value: 'coding_challenge' },
		{ label: 'Technical Interview', value: 'technical_interview' },
		{ label: 'Follow Up', value: 'follow_up' },
		{ label: 'Profile Approval', value: 'profile_approval' },
		{ label: 'Note', value: 'note' },
		{ label: 'Team Transfer', value: 'team_transfer' }
	];

	publicProfileStatusOptions = [
		{ label: 'No Public Profile', value: 'none' },
		{ label: 'Created, not registered', value: 'unclaimed' },
		{ label: 'Registered in MIRA', value: 'claimed' }
	];

	schedulableTaskTypes = [
		'hr_interview',
		'coding_challenge',
		'technical_interview'
	];

	stageOptions = [
		{
			label: 'New',
			value: 'new',
			level: 0
		},
		{
			label: 'HR Assessment',
			value: 'hr_interview',
			level: 1
		},
		{
			label: 'General Tests',
			value: 'general_tests',
			level: 2
		},
		{
			label: 'Technical Evaluation',
			value: 'technical_interview',
			level: 3
		},
		{
			label: 'Profile Polishing',
			value: 'profile_polishing',
			level: 4
		},
		{
			label: 'Completed',
			value: 'completed',
			level: 5
		},
		{
			label: 'Technical Gap',
			value: 'discarded_technical_skills',
			level: 5
		},
		{
			label: 'People Unfit',
			value: 'discarded_people_skills',
			level: 5
		},
		{
			label: 'Cultural Unfit',
			value: 'discarded_cultural_unfit',
			level: 5
		},
		{
			label: 'Out of reach',
			value: 'candidate_out_of_reach',
			level: 5
		},
		{
			label: 'Rejected the Process',
			value: 'rejected_the_process',
			level: 5
		}
	];

	statusOptions = [
		{ label: 'Available', value: 'available', color: '#6172F3' },
		{ label: 'Discarded', value: 'discarded', color: '#F63D68' },
		{ label: 'Dropped-out', value: 'dropped_out', color: '#D5D7DA' }
	];

	stageToStatus = {
		new: 'available',
		hr_interview: 'available',
		general_tests: 'available',
		technical_interview: 'available',
		profile_polishing: 'available',
		completed: 'available',
		discarded_technical_skills: 'discarded',
		discarded_people_skills: 'discarded',
		discarded_cultural_unfit: 'discarded',
		candidate_out_of_reach: 'dropped_out',
		rejected_the_process: 'dropped_out'
	};

	defaultTableColumnsOptions = [
		{ label: 'Name', value: 'name' },
		{ label: 'Linkedin', value: 'linkedin' },
		{ label: 'Internal Stage', value: 'internal_stage' },
		{ label: 'Latest Task', value: 'latest_task' },
		{ label: 'Hireability', value: 'hireability' },
		{
			label: 'Expected Compensation',
			value: 'expected_compensation'
		},
		{
			label: 'Seniority',
			value: 'seniority'
		},
		{
			label: 'Country',
			value: 'country'
		},
		{
			label: 'Owner',
			value: 'owner'
		}
	];

	tableColumnsOptions = [
		{ label: 'Name', value: 'name' },
		{ label: 'Linkedin', value: 'linkedin' },
		{ label: 'Internal Stage', value: 'internal_stage' },
		{ label: 'Latest Task', value: 'latest_task' },
		{ label: 'Last Engagement Date', value: 'last_engagement_date' },
		{ label: 'Source', value: 'source' },
		{ label: 'Hireability', value: 'hireability' },
		{
			label: 'Current Compensation',
			value: 'current_compensation'
		},
		{
			label: 'Expected Compensation',
			value: 'expected_compensation'
		},
		{
			label: 'Seniority',
			value: 'seniority'
		},
		{ label: 'English Level', value: 'english_level' },
		{
			label: 'Country',
			value: 'country'
		},
		{
			label: 'Owner',
			value: 'owner'
		},
		{ label: 'Created At', value: 'created_at' },
		{ label: 'Updated At', value: 'updated_at' },
		{ label: 'Availability', value: 'availability' },
		{ label: 'Email', value: 'email' },
		{ label: 'Phone Number', value: 'phone_number' },
		{ label: 'City', value: 'city' },
		{ label: 'Github', value: 'github_url' },
		{ label: 'Website', value: 'portfolio_url' }
	];

	compensationsOptions = [
		{ label: 'Current', value: 'current' },
		{ label: 'Expected', value: 'expected' }
	];

	compensationsTypesOptions = [
		{ label: 'Hourly', value: 'hourly' },
		{ label: 'Monthly', value: 'monthly' }
	];

	featuredTagsOptions = ['Alumni', 'High Potential', 'Rockstar', 'Blacklisted'];

	featuredTagsIcons = {
		alumni: 'alumni-icon',
		'high potential': 'high-potential-icon',
		rockstar: 'rockstar-icon',
		blacklisted: 'blacklisted-icon'
	};

	getOptionByValue = (options, value) => {
		return options.find(option => option.value === value);
	};

	getTaskByValue = value => {
		return this.taskOptions.find(option => option.value === value);
	};

	@action
	async loadApplicants(reset = false) {
		const result = await this.search(reset);
		runInAction(() => {
			this.applicants = result.data.rows;
		});
	}

	@action
	async getApplicants(query = {}) {
		const pagination =
			query && query.pagination
				? query.pagination
				: {
						page: 1,
						limit: 20
					};

		const result = await api.post(`${this.entityPluralEndpoint}/query`, {
			query: {
				...query,
				pagination
			}
		});

		return result;
	}

	@action
	async assignCandidate({ applicantId, candidateId }) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/${applicantId}/candidates/${candidateId}`
		);

		return result;
	}

	@action
	async createPublicProfile(applicantId) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/${applicantId}/public-profile`
		);

		return result;
	}

	@action
	async getInviteLink(applicantId) {
		const result = await api.get(
			`${this.entityPluralEndpoint}/${applicantId}/invite-link`
		);

		return result;
	}

	@action
	async inviteCandidate({ applicantId, email }) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/${applicantId}/invite-link`,
			{ email }
		);

		return result;
	}

	@action
	async loadFeaturedTags() {
		try {
			const promises = this.featuredTagsOptions.map(async tagName => {
				const result = await api.get(`/tags?q=${encodeURIComponent(tagName)}`);
				return result.data.find(tag => tag.name === tagName);
			});

			const tags = await Promise.all(promises);

			return tags;
		} catch (error) {
			console.error('Error loading featured tags:', error);

			return [];
		}
	}

	@action
	async createTask(applicantId, request) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/${applicantId}/tasks`,
			request
		);
		return result;
	}

	@action
	async updateTask(applicantId, taskId, request) {
		const result = await api.put(
			`${this.entityPluralEndpoint}/${applicantId}/tasks/${taskId}`,
			request
		);
		return result;
	}

	@action
	async pinTask(applicantId, taskId, request) {
		const result = await api.put(
			`${this.entityPluralEndpoint}/${applicantId}/tasks/${taskId}/pin`,
			request
		);
		return result;
	}

	@action
	async getTasks(request) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/tasks`,
			request
		);
		return result;
	}

	@action
	async deleteTask(applicantId, taskId) {
		const result = await api.delete(
			`${this.entityPluralEndpoint}/${applicantId}/tasks/${taskId}`
		);
		return result;
	}

	@override
	async create(request) {
		if (request.profileImage && request.profileImage instanceof File) {
			const config = {
				headers: {
					'content-type': 'multipart/form-data'
				}
			};
			const formData = new FormData();

			buildFormData(formData, request);

			const result = await api.post(
				this.entityPluralEndpoint,
				formData,
				config
			);
			return result;
		} else {
			return super.create(request);
		}
	}

	@override
	async update(id, request) {
		if (
			request.applicant.profileImage &&
			request.applicant.profileImage instanceof File
		) {
			const config = {
				headers: {
					'content-type': 'multipart/form-data'
				}
			};
			const formData = new FormData();

			buildFormData(formData, request);

			const result = await api.put(
				`${this.entityPluralEndpoint}/${id}`,
				formData,
				config
			);
			return result;
		} else {
			return super.update(id, request);
		}
	}

	@action
	async uploadApplicantImage(applicantId, imageFile) {
		const config = {
			headers: {
				'content-type': 'multipart/form-data'
			}
		};
		const formData = new FormData();
		formData.append('image', imageFile);
		const result = await api.put(
			`${this.entityPluralEndpoint}/${applicantId}/image`,
			formData,
			config
		);
		return result;
	}

	@action
	async checkDuplicate({ email, linkedinUrl, excludeId } = {}) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/check-duplicate`,
			{
				email,
				linkedinUrl,
				excludeId
			}
		);
		return result;
	}

	@action
	async bulkUpdate(ids, applicant) {
		const result = await api.post(`${this.entityPluralEndpoint}/bulk/update`, {
			ids,
			applicant
		});
		return result;
	}

	@action
	async bulkDelete(ids) {
		const result = await api.post(`${this.entityPluralEndpoint}/bulk/delete`, {
			ids
		});
		return result;
	}

	@action
	async getResumes(applicantId) {
		const result = await api.get(
			`${this.entityPluralEndpoint}/${applicantId}/resumes`
		);
		return result;
	}

	@action
	async uploadResume(applicantId, resumeFile) {
		const config = {
			headers: {
				'content-type': 'multipart/form-data'
			}
		};
		const formData = new FormData();
		formData.append('resume', resumeFile);
		const result = await api.post(
			`${this.entityPluralEndpoint}/${applicantId}/resumes`,
			formData,
			config
		);
		return result;
	}

	@action
	async downloadResume(applicantId, resumeId) {
		const result = await api.get(
			`${this.entityPluralEndpoint}/${applicantId}/resumes/${resumeId}/download`,
			{
				responseType: 'blob'
			}
		);
		return result;
	}

	@action
	async deleteResume(applicantId, resumeId) {
		const result = await api.delete(
			`${this.entityPluralEndpoint}/${applicantId}/resumes/${resumeId}`
		);
		return result;
	}
}

export const applicantStore = new ApplicantStore();
